import React, { useState, useEffect, useCallback } from 'react';
import {
  FaNetworkWired, FaEnvelope, FaUserCircle, FaChartLine, FaTools, FaCalculator,
  FaSpinner, FaUnlock, FaKey, FaRedo,
} from 'react-icons/fa';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import './FormModal.css';
import './VacantesUI.css';
import './CuentasModal.css';

const CLAVE_TEMPORAL = 'sura2027*';

const SISTEMAS = [
  { clave: 'red', nombre: 'Red', detalle: 'Active Directory', icono: FaNetworkWired },
  { clave: 'correo', nombre: 'Correo', detalle: 'Google Workspace', icono: FaEnvelope },
  { clave: 'centro_sura', nombre: 'Centro Sura', detalle: 'Este portal', icono: FaUserCircle },
  { clave: 'cima', nombre: 'CIMA', detalle: 'Ventas', icono: FaChartLine },
  { clave: 'soluciones_ti', nombre: 'Soluciones TI', detalle: 'Herramientas', icono: FaTools },
  { clave: 'profit', nombre: 'Profit', detalle: 'Administrativo y contable', icono: FaCalculator },
];

const fecha = (valor) => {
  if (!valor) return null;
  const f = new Date(valor);
  if (Number.isNaN(f.getTime())) return valor;
  return f.toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Las cuentas de una persona en cada sistema y su estado, sin claves.
 * Cada tarjeta se pide aparte: un sistema lento o caído no tranca a los demás.
 * TI (`puedeGestionar`) además puede desbloquear la de red y restablecer su clave.
 */
function CuentasModal({ empleado, onClose, puedeGestionar = false }) {
  const [cuentas, setCuentas] = useState({}); // clave -> { cargando, datos, error }
  const [gruposAbiertos, setGruposAbiertos] = useState(null);
  const [trabajando, setTrabajando] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [errorAccion, setErrorAccion] = useState(null);
  const [mostrarClave, setMostrarClave] = useState(false);
  const [clave, setClave] = useState(CLAVE_TEMPORAL);
  const [cambiarAlEntrar, setCambiarAlEntrar] = useState(true);

  const consultar = useCallback(async (sistema) => {
    setCuentas((c) => ({ ...c, [sistema]: { ...c[sistema], cargando: true, error: null } }));
    try {
      const r = await axiosInstance.get(`/api/empleados/${empleado.id}/cuentas/${sistema}`);
      setCuentas((c) => ({ ...c, [sistema]: { cargando: false, datos: r.data, error: null } }));
    } catch (err) {
      setCuentas((c) => ({
        ...c,
        [sistema]: { cargando: false, datos: null, error: err.response?.data?.error || 'No se pudo consultar' },
      }));
    }
  }, [empleado.id]);

  useEffect(() => {
    SISTEMAS.forEach((s) => consultar(s.clave));
  }, [consultar]);

  const accionRed = async (tipo) => {
    setTrabajando(tipo);
    setErrorAccion(null);
    setAviso(null);
    try {
      const r = tipo === 'desbloquear'
        ? await axiosInstance.post(`/api/empleados/${empleado.id}/cuentas/red/desbloquear`)
        : await axiosInstance.post(`/api/empleados/${empleado.id}/cuentas/red/restablecer-clave`, {
          clave, cambiar_al_entrar: cambiarAlEntrar,
        });
      setAviso(tipo === 'clave'
        ? `${r.data.message}.${cambiarAlEntrar ? ' Le pedirá cambiarla al iniciar sesión.' : ''}`
        : r.data.message);
      setMostrarClave(false);
      consultar('red');
    } catch (err) {
      setErrorAccion(err.response?.data?.error || 'No se pudo completar la acción');
    } finally {
      setTrabajando(null);
    }
  };

  const listos = SISTEMAS.filter((s) => cuentas[s.clave] && !cuentas[s.clave].cargando);
  const activas = listos.filter((s) => cuentas[s.clave].datos?.existe && cuentas[s.clave].datos?.activa !== false).length;
  const faltantes = listos.filter((s) => cuentas[s.clave].datos && !cuentas[s.clave].datos.existe && cuentas[s.clave].datos.pedida);

  const pill = (d) => {
    if (!d.existe) return <span className="vac-estado vac-estado--cancelada">No tiene</span>;
    if (d.activa === false) return <span className="vac-estado bg-red-50 text-red-700">{d.alerta?.startsWith('Suspendida') ? 'Suspendida' : 'Deshabilitada'}</span>;
    if (d.bloqueada) return <span className="vac-estado bg-red-50 text-red-700">Bloqueada</span>;
    if (d.activa === null) return <span className="vac-estado vac-estado--en-proceso">Existe</span>;
    return <span className="vac-estado vac-estado--cubierta">Activa</span>;
  };

  const tarjeta = (s) => {
    const estado = cuentas[s.clave] || { cargando: true };
    const d = estado.datos;
    const Icono = s.icono;
    return (
      <article key={s.clave} className={`cta-tarjeta${d && !d.existe ? ' cta-tarjeta--vacia' : ''}`}>
        <header className="cta-cabecera">
          <span className="cta-icono"><Icono /></span>
          <span className="flex-1 min-w-0">
            <span className="cta-nombre">{s.nombre}</span>
            <span className="cta-detalle">{s.detalle}</span>
          </span>
          {d && pill(d)}
        </header>

        {estado.cargando ? (
          <div className="cta-cargando" aria-label={`Consultando ${s.nombre}`}>
            <span /><span /><span />
          </div>
        ) : estado.error ? (
          <div className="cta-error">
            <p>No se pudo consultar {s.nombre}.</p>
            <p className="cta-error-detalle">{estado.error}</p>
            <button type="button" className="vac-link-btn inline-flex items-center gap-1" onClick={() => consultar(s.clave)}>
              <FaRedo /> Reintentar
            </button>
          </div>
        ) : !d.existe ? (
          <div className="cta-vacio">
            {d.usuario && <p className="cta-usuario">{d.usuario}</p>}
            {d.pedida
              ? <p className="text-amber-700">La ficha TI la pidió y no existe.</p>
              : <p>{d.alerta || 'No tiene cuenta.'}</p>}
          </div>
        ) : (
          <>
            <p className="cta-usuario">{d.usuario}</p>
            {d.alerta && <p className="cta-alerta">{d.alerta}</p>}
            <dl className="cta-datos">
              {d.detalles.map(([etiqueta, valor, tipo]) => (
                <div key={etiqueta}>
                  <dt>{etiqueta}</dt>
                  <dd>
                    {tipo === 'fecha' ? fecha(valor) : String(valor)}
                    {etiqueta === 'Grupos' && d.grupos?.length > 0 && (
                      <button
                        type="button"
                        className="vac-link-btn ml-2 text-xs"
                        onClick={() => setGruposAbiertos(gruposAbiertos === s.clave ? null : s.clave)}
                      >
                        {gruposAbiertos === s.clave ? 'Ocultar' : 'Ver'}
                      </button>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {gruposAbiertos === s.clave && (
              <div className="cta-grupos">
                {d.grupos.map((g) => <span key={g} className="exp-chip">{g}</span>)}
              </div>
            )}

            {s.clave === 'red' && puedeGestionar && (
              <div className="cta-acciones">
                {d.bloqueada && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => accionRed('desbloquear')} disabled={!!trabajando}>
                    {trabajando === 'desbloquear' ? <FaSpinner className="animate-spin" /> : <><FaUnlock /> Desbloquear</>}
                  </button>
                )}
                {!mostrarClave && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMostrarClave(true)} disabled={!!trabajando}>
                    <FaKey /> Restablecer clave
                  </button>
                )}
                {mostrarClave && (
                  <div className="cta-clave">
                    <label htmlFor="clave-temporal" className="text-xs text-gray-600">Clave temporal</label>
                    <input
                      id="clave-temporal"
                      className="input py-1.5"
                      type="text"
                      value={clave}
                      onChange={(e) => setClave(e.target.value)}
                      autoComplete="off"
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                      <input type="checkbox" checked={cambiarAlEntrar} onChange={(e) => setCambiarAlEntrar(e.target.checked)} />
                      Pedir que la cambie al iniciar sesión
                    </label>
                    <div className="flex gap-2">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMostrarClave(false)}>Cancelar</button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => accionRed('clave')}
                        disabled={trabajando === 'clave' || clave.trim().length < 8}
                      >
                        {trabajando === 'clave' ? <FaSpinner className="animate-spin" /> : 'Restablecer'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </article>
    );
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="2xl">
      <Modal.Header onClose={onClose}>Cuentas de {empleado.nombre}</Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-600 mb-4">
          {listos.length < SISTEMAS.length
            ? 'Consultando cada sistema...'
            : `${activas} de ${SISTEMAS.length} cuentas activas.`}
          {faltantes.length > 0 && (
            <span className="text-amber-700">
              {' '}Falta crear: {faltantes.map((s) => s.nombre).join(', ')}.
            </span>
          )}
        </p>
        {aviso && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">{aviso}</div>}
        {errorAccion && <div className="form-modal-error mb-4">{errorAccion}</div>}
        <div className="cta-rejilla">
          {SISTEMAS.map(tarjeta)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
      </Modal.Footer>
    </Modal>
  );
}

export default CuentasModal;
